import { StyleSheet, css } from 'aphrodite';
import { type FC, useState } from 'react';
import type { CoachMessage } from '../ai';
import { PinnedCard } from './PinnedCard';

/** Tiles beyond this stay pinned but demote to chips; four is what fits at a readable width. */
export const MAX_VISIBLE_TILES = 4;

/**
 * Above this many tiles the Game Plan gives up its full-height column and the grid becomes an even
 * 2x2. Reps asked for the tall column while there is room to read everything at once, and for the
 * even split once the screen fills up.
 */
const COLLAPSE_AT = MAX_VISIBLE_TILES;

export type PinTile = {
	msg: CoachMessage;
	/**
	 * `lead` is the Game Plan — the tile that holds the tall column until the grid collapses. `live` is
	 * the streaming coaching suggestion, which always sorts last so it lands in a predictable spot.
	 */
	kind: 'lead' | 'live' | 'pin';
};

export type PinGridProps = {
	/** Visible order, Game Plan first. The parent decides ordering; the grid only decides shape. */
	tiles: PinTile[];
	onUnpin: (messageId: string) => void;
	/** Bring a demoted (chip) tile back into the grid. */
	onPromote?: (messageId: string) => void;
	/** True while the AI is streaming, which drives the live tile's indicator. */
	isResponding?: boolean;
	wsConnected?: boolean;
};

/**
 * The pinned-tile grid in the middle of the focused view.
 *
 * Replaces the old single scrolling column, where the hero's 40vh cap plus a fixed-height Game Plan
 * left every other pin sharing one short well at the bottom — and three pins arrive on a normal call
 * before the rep touches anything.
 *
 * Shape is driven by how many tiles are showing, which is the pressure reps actually feel:
 *  - one tile fills the area,
 *  - two or three keep the Game Plan in a full-height left column with the rest stacked beside it,
 *  - four collapse to an even 2x2.
 * Anything past four demotes to a chip strip rather than shrinking every tile further. Each tile
 * scrolls internally, and any tile can be maximized to fill the grid for a long read.
 *
 * `data-layout` names the current shape so tests and debugging do not have to read grid CSS.
 */
export const PinGrid: FC<PinGridProps> = ({ tiles, onUnpin, onPromote, isResponding, wsConnected }) => {
	const [maximizedId, setMaximizedId] = useState<string | null>(null);

	const visible = tiles.slice(0, MAX_VISIBLE_TILES);
	const demoted = tiles.slice(MAX_VISIBLE_TILES);

	// A maximized tile that gets unpinned (or falls off on a lead switch) must not leave the grid stuck
	// showing nothing, so treat a missing id as not-maximized rather than tracking it in an effect.
	const maximized = visible.find(tile => tile.msg.id === maximizedId) ?? null;

	const renderTile = (tile: PinTile, isMaximized: boolean) => (
		<PinnedCard
			key={tile.msg.id}
			msg={tile.msg}
			elevated={tile.kind === 'lead'}
			live={tile.kind === 'live'}
			streaming={tile.kind === 'live' && isResponding}
			wsConnected={tile.kind === 'live' ? wsConnected : undefined}
			maximized={isMaximized}
			onToggleMaximize={() => setMaximizedId(isMaximized ? null : tile.msg.id)}
			onUnpin={() => onUnpin(tile.msg.id)}
		/>
	);

	if (maximized) {
		return (
			<div className={css(styles.grid, styles.single)} data-layout='maximized'>
				<div className={css(styles.cell)}>{renderTile(maximized, true)}</div>
			</div>
		);
	}

	if (visible.length === 0) {
		return (
			<div className={css(styles.grid, styles.single)} data-layout='empty'>
				<EmptyPins />
			</div>
		);
	}

	if (visible.length === 1) {
		return (
			<div className={css(styles.grid, styles.single)} data-layout='single'>
				<div className={css(styles.cell)}>{renderTile(visible[0], false)}</div>
				<DemotedStrip tiles={demoted} onPromote={onPromote} />
			</div>
		);
	}

	const [first, ...rest] = visible;

	if (visible.length < COLLAPSE_AT) {
		return (
			<div
				className={css(styles.grid, styles.twoColumn)}
				style={{ gridTemplateRows: `repeat(${rest.length}, minmax(0, 1fr))` }}
				data-layout='weighted'
			>
				<div className={css(styles.cell)} style={{ gridColumn: 1, gridRow: '1 / -1' }}>
					{renderTile(first, false)}
				</div>
				{rest.map(tile => (
					<div key={tile.msg.id} className={css(styles.cell)} style={{ gridColumn: 2 }}>
						{renderTile(tile, false)}
					</div>
				))}
				<DemotedStrip tiles={demoted} onPromote={onPromote} />
			</div>
		);
	}

	return (
		<div className={css(styles.grid, styles.twoColumn, styles.twoRow)} data-layout='even'>
			{visible.map(tile => (
				<div key={tile.msg.id} className={css(styles.cell)}>
					{renderTile(tile, false)}
				</div>
			))}
			<DemotedStrip tiles={demoted} onPromote={onPromote} />
		</div>
	);
};

/**
 * Tiles past the visible cap. Kept visible as chips so the rep can see what is still pinned and pull
 * one back, instead of it silently vanishing the way overflow did in the old scrolling column.
 */
const DemotedStrip: FC<{ tiles: PinTile[]; onPromote?: (messageId: string) => void }> = ({ tiles, onPromote }) => {
	if (tiles.length === 0) {
		return null;
	}

	return (
		<div className={css(styles.demotedStrip)} data-testid='demoted-pins'>
			<span className={css(styles.demotedLabel)}>ALSO PINNED</span>
			{tiles.map(tile => (
				<button
					key={tile.msg.id}
					type='button'
					className={css(styles.demotedChip)}
					onClick={() => onPromote?.(tile.msg.id)}
					title='Show this pin in the grid'
				>
					<span className={css(styles.demotedDot)} style={{ background: tile.msg.pinColor || 'var(--accent)' }} />
					{(tile.msg.pinnedKeyword || 'Pinned').toUpperCase()}
				</button>
			))}
		</div>
	);
};

const EmptyPins: FC = () => (
	<div className={css(styles.empty)}>Hover any AI message and click the pin icon to keep it here for the call.</div>
);

const styles = StyleSheet.create({
	grid: {
		flex: 1,
		minHeight: 0,
		display: 'grid',
		gap: 10,
		padding: '14px 20px 16px',
	},
	single: {
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: 'minmax(0, 1fr)',
	},
	// Two columns, never three: at 1900px the centre pane is about 1500px, so a third column drops each
	// tile to roughly 490px, which is too narrow for the markdown these cards hold.
	twoColumn: {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
	twoRow: {
		gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
	},
	cell: {
		display: 'flex',
		minHeight: 0,
		minWidth: 0,
	},
	demotedStrip: {
		gridColumn: '1 / -1',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 6,
	},
	demotedLabel: {
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.1em',
		color: 'var(--ink-faint)',
	},
	demotedChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.06em',
		padding: '4px 9px',
		borderRadius: 999,
		border: '1px solid var(--border)',
		background: 'var(--bg-elev)',
		color: 'var(--ink-muted)',
		cursor: 'pointer',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	demotedDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		flexShrink: 0,
	},
	empty: {
		alignSelf: 'flex-start',
		padding: '20px 16px',
		border: '1px dashed var(--border)',
		borderRadius: 10,
		textAlign: 'center',
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		lineHeight: 1.5,
	},
});
